import React, { useState, useRef, useEffect } from 'react';
import {
  countries,
  getStatesForCountry,
  type Country,
} from '../../data/location';

interface LocationAutocompleteProps {
  type: 'country' | 'state';
  value: string;
  onChange: (value: string) => void;
  country?: string; // Required when type is 'state' - to know which states to show
  onCountryChange?: (country?: string) => void; // When type is 'country', called on select
  placeholder?: string;
  className?: string;
  inputClassName?: string; // Applied to the input element
  required?: boolean;
  excludeOptions?: string[]; // Options to exclude from the list
  disabled?: boolean;
}

function filterCountries(
  options: typeof countries,
  query: string,
  exclude: string[] = [],
): typeof countries {
  let filtered = options;
  if (exclude.length > 0) {
    filtered = filtered.filter((c) => !exclude.includes(c.name));
  }
  if (!query.trim()) return filtered;
  const lower = query.toLowerCase();
  return filtered.filter((c) => {
    if (c.name.toLowerCase().includes(lower)) return true;
    if (c.aliases?.some((a) => a.toLowerCase().includes(lower))) return true;
    return false;
  });
}

function filterStrings(
  options: string[],
  query: string,
  exclude: string[] = [],
): string[] {
  let filtered = options;
  if (exclude.length > 0) {
    filtered = filtered.filter((s) => !exclude.includes(s));
  }
  if (!query.trim()) return filtered;
  const lower = query.toLowerCase();
  return filtered.filter((s) => s.toLowerCase().includes(lower));
}

export const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  type,
  value,
  onChange,
  country = '',
  onCountryChange,
  placeholder = type === 'country'
    ? 'Select or type country'
    : 'Select or type state',
  className = '',
  inputClassName = '',
  required = false,
  excludeOptions = [],
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);

  const stateOptions = type === 'state' ? getStatesForCountry(country) : null;
  const showStateAutocomplete = type === 'state' && stateOptions !== null;

  // Sync input value when value prop changes (e.g. when country changes and state is cleared)
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCountrySelect = (c: Country) => {
    if (disabled) return;
    onChange(c.name);
    setInputValue(c.name);
    setIsOpen(false);
    onCountryChange?.(c.name);
  };

  const handleStateSelect = (s: string) => {
    if (disabled) return;
    onChange(s);
    setInputValue(s);
    setIsOpen(false);
  };

  if (type === 'country') {
    const filtered = filterCountries(countries, inputValue, excludeOptions);
    return (
      <div ref={containerRef} className={`relative ${className}`}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            if (disabled) return;
            setInputValue(e.target.value);
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => !disabled && setIsOpen(true)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          autoComplete="off"
          className={`mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-herbal-green focus:ring-herbal-green px-3 py-2 pr-10 text-gray-900 placeholder-gray-400 outline-none disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed ${inputClassName}`}
        />
        {inputValue && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setInputValue('');
              onChange('');
              setIsOpen(false);
            }}
            className="absolute right-3 top-[calc(50%+2px)] -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
            title="Clear"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
        {isOpen && (
          <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-500">
                No matches. You can type any country name.
              </li>
            ) : (
              filtered.slice(0, 50).map((c) => (
                <li
                  key={c.isoCode}
                  role="option"
                  tabIndex={0}
                  className="cursor-pointer px-3 py-2 text-sm hover:bg-herbal-green/10 focus:bg-herbal-green/10 focus:outline-none"
                  onClick={() => handleCountrySelect(c)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCountrySelect(c);
                  }}
                >
                  {c.name}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    );
  }

  // State field
  if (showStateAutocomplete) {
    const filtered = filterStrings(stateOptions, inputValue, excludeOptions);
    return (
      <div ref={containerRef} className={`relative ${className}`}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            if (disabled) return;
            setInputValue(e.target.value);
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => !disabled && setIsOpen(true)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          autoComplete="off"
          className={`mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-herbal-green focus:ring-herbal-green px-3 py-2 pr-10 text-gray-900 placeholder-gray-400 outline-none disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed ${inputClassName}`}
        />
        {inputValue && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setInputValue('');
              onChange('');
              setIsOpen(false);
            }}
            className="absolute right-3 top-[calc(50%+2px)] -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
            title="Clear"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
        {isOpen && (
          <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-500">
                No matches. You can type any state name.
              </li>
            ) : (
              filtered.slice(0, 50).map((s) => (
                <li
                  key={s}
                  role="option"
                  tabIndex={0}
                  className="cursor-pointer px-3 py-2 text-sm hover:bg-herbal-green/10 focus:bg-herbal-green/10 focus:outline-none"
                  onClick={() => handleStateSelect(s)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleStateSelect(s);
                  }}
                >
                  {s}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    );
  }

  // State field when country is not India/US - free text
  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={`mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-herbal-green focus:ring-herbal-green px-3 py-2 pr-10 text-gray-900 placeholder-gray-400 outline-none disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed ${inputClassName}`}
      />
      {value && !disabled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChange('');
          }}
          className="absolute right-3 top-[calc(50%+2px)] -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
          title="Clear"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default LocationAutocomplete;
